import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { isSignInWithEmailLink, sendSignInLinkToEmail, signInWithEmailLink } from 'firebase/auth';
import { auth, functions } from '../lib/firebase';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Field, TextInput } from '../components/ui/Field';
import { usePageTitle } from '../hooks/usePageTitle';
import { ROLE_LABELS, type MemberRole } from '../types/models';

interface InvitationRequest {
  companyId: string;
  invitationId: string;
  token: string;
}

interface InvitationDetails {
  email: string;
  role: MemberRole;
  companyName: string | null;
  expiresAt: string;
}

type Step = 'loading' | 'invalid' | 'intro' | 'sent' | 'confirm-email' | 'password' | 'done';

// Which address THIS browser asked Firebase to email a sign-in link to, per
// invitation. Finishing an email-link sign-in with an address taken from the
// link itself would let someone hand you a link that signs you in as them
// (Firebase calls this session injection), so the only other way to finish is
// to have the person type their address.
const EMAIL_FOR_SIGN_IN_KEY = 'checkover.invitationSignInEmail';

function rememberSignInEmail(invitationId: string, email: string) {
  try {
    localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, JSON.stringify({ invitationId, email }));
  } catch {
    /* private mode: the person types their address instead */
  }
}

function rememberedSignInEmail(invitationId: string): string | null {
  try {
    const stored = JSON.parse(localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY) ?? 'null') as
      | { invitationId?: string; email?: string }
      | null;
    return stored?.invitationId === invitationId && stored.email ? stored.email : null;
  } catch {
    return null;
  }
}

function forgetSignInEmail() {
  try {
    localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
  } catch {
    /* noop */
  }
}

function friendlyError(err: unknown, fallback: string): string {
  const code = (err as { code?: string } | null)?.code ?? '';
  if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
    return 'This sign-in link has expired or has already been used. Ask for a new one below.';
  }
  if (code === 'auth/invalid-email') {
    return "That isn't the address this sign-in link was sent to.";
  }
  if (code === 'auth/user-disabled') {
    return 'This login has been disabled. Contact whoever invited you.';
  }
  if (code.startsWith('functions/') && err instanceof Error && err.message) {
    return err.message; // the server's own wording, e.g. "This invitation has expired"
  }
  return fallback;
}

// P0-01/N3: accepting an invitation needs two things. The invitation link, which
// the inviting admin also holds because they send it. And proof of owning the
// invited address: Firebase emails a sign-in link there, and only its owner can
// open it. The server (acceptInvitation) refuses anyone else, so holding this
// page's link is not enough on its own.
export default function AcceptInvitationPage() {
  usePageTitle('Accept invitation');
  const [searchParams] = useSearchParams();
  const { user, signIn, refreshMemberships } = useAuth();
  const navigate = useNavigate();

  const companyId = searchParams.get('company') ?? '';
  const invitationId = searchParams.get('invitation') ?? '';
  const token = searchParams.get('token') ?? '';
  const invitation: InvitationRequest = useMemo(
    () => ({ companyId, invitationId, token }),
    [companyId, invitationId, token]
  );
  // Where the emailed sign-in link brings the person back to: this page, for this invitation.
  const invitationUrl = useMemo(() => {
    const query = new URLSearchParams({ company: companyId, invitation: invitationId, token });
    return `${window.location.origin}/accept-invitation?${query.toString()}`;
  }, [companyId, invitationId, token]);

  const [step, setStep] = useState<Step>('loading');
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [typedEmail, setTypedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsNewLink, setNeedsNewLink] = useState(false);
  const started = useRef(false);

  const prepare = (ensureAccount: boolean) =>
    httpsCallable<InvitationRequest & { ensureAccount: boolean }, InvitationDetails>(
      functions,
      'prepareInvitationAcceptance'
    )({ ...invitation, ensureAccount }).then((result) => result.data);

  async function finishEmailLinkSignIn(email: string) {
    await signInWithEmailLink(auth, email, window.location.href);
    forgetSignInEmail();
    // Drop the one-time code from the address bar so a refresh can't replay it.
    window.history.replaceState(null, '', invitationUrl.slice(window.location.origin.length));
    setError(null);
    setNeedsNewLink(false);
    setStep('password');
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!companyId || !invitationId || !token) {
      setError("This invitation link is missing information and can't be used. Ask whoever invited you to send it again.");
      setStep('invalid');
      return;
    }

    void (async () => {
      let loaded: InvitationDetails;
      try {
        loaded = await prepare(false);
        setDetails(loaded);
      } catch (err) {
        setError(friendlyError(err, "This invitation link can't be used. Ask whoever invited you to send it again."));
        setStep('invalid');
        return;
      }

      if (!isSignInWithEmailLink(auth, window.location.href)) {
        setStep('intro');
        return;
      }

      const remembered = rememberedSignInEmail(invitationId);
      if (!remembered) {
        setStep('confirm-email');
        return;
      }
      try {
        await finishEmailLinkSignIn(remembered);
      } catch (err) {
        setError(friendlyError(err, "We couldn't sign you in with that link. Ask for a new one below."));
        setStep('intro');
      }
    })();
    // Runs once per page load; the invitation comes from the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendLink() {
    if (!details) return;
    setBusy(true);
    setError(null);
    try {
      await prepare(true);
      await sendSignInLinkToEmail(auth, details.email, { url: invitationUrl, handleCodeInApp: true });
      rememberSignInEmail(invitationId, details.email);
      setStep('sent');
    } catch (err) {
      setError(friendlyError(err, "We couldn't send the sign-in link. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function confirmEmail(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await finishEmailLinkSignIn(typedEmail.trim());
    } catch (err) {
      setError(friendlyError(err, "We couldn't sign you in with that link."));
    } finally {
      setBusy(false);
    }
  }

  async function accept(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!details) return;
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await httpsCallable<InvitationRequest & { password: string }, { success: boolean }>(
        functions,
        'acceptInvitation'
      )({ ...invitation, password });
      // Setting the password ends the email-link session; carry on with the new one.
      await signIn(details.email, password);
      setStep('done');
    } catch (err) {
      const message = friendlyError(err, "We couldn't accept the invitation. Please try again.");
      setError(message);
      // The server wants a fresh sign-in by email link (stale or unverified session).
      setNeedsNewLink(/too old|Confirm your email/i.test(message));
    } finally {
      setBusy(false);
    }
  }

  async function continueToApp() {
    setBusy(true);
    await refreshMemberships();
    navigate('/', { replace: true });
  }

  const companyLabel = details?.companyName ?? 'the company';
  const subtitle: Record<Step, string> = {
    loading: 'Checking your invitation…',
    invalid: 'This invitation link can’t be used.',
    intro: 'You’ve been invited to Checkover.',
    sent: 'Check your email.',
    'confirm-email': 'Confirm your email address.',
    password: 'Choose your password.',
    done: 'You’re all set.',
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-10">
      <div className="w-full max-w-[420px]">
        <div className="mb-7 text-center">
          <img src="/checkover-icon.png" alt="" className="mx-auto mb-4 h-[72px] w-[72px] rounded-[16px]" />
          <h1 className="text-[30px] font-bold tracking-[-0.035em] text-ink">Checkover</h1>
          <p className="mt-1.5 text-[15px] text-ink2">{subtitle[step]}</p>
        </div>

        <div className="flex flex-col gap-4 rounded-[14px] border border-hair bg-elev p-6 text-[15px] text-ink2 sm:p-7">
          {step === 'loading' && <p className="text-center">One moment…</p>}

          {step === 'invalid' && (
            <>
              <Alert tone="error">{error}</Alert>
              <Button variant="outline" onClick={() => navigate('/login')}>Go to sign in</Button>
            </>
          )}

          {step === 'intro' && details && (
            <>
              <p>
                <strong className="text-ink">{details.companyName ?? 'A company'}</strong> has invited you to join
                Checkover as <strong className="text-ink">{ROLE_LABELS[details.role] ?? details.role}</strong>.
              </p>
              <p>
                To make sure this invitation reaches the right person, we’ll email a sign-in link to{' '}
                <strong className="break-all text-ink">{details.email}</strong>.
              </p>
              {user && user.email?.toLowerCase() !== details.email.toLowerCase() && (
                <Alert tone="warning">
                  You’re signed in as {user.email}. Finishing this invitation will sign you in as {details.email} instead.
                </Alert>
              )}
              {error && <Alert tone="error">{error}</Alert>}
              <Button size="lg" loading={busy} onClick={() => void sendLink()} className="w-full">
                Email me a sign-in link
              </Button>
            </>
          )}

          {step === 'sent' && details && (
            <>
              <Alert tone="success">Sign-in link sent to {details.email}.</Alert>
              <p>
                Open the email and tap the link. It works on this device or another one, and brings you back here to
                choose your password. If it hasn’t arrived in a few minutes, check your spam folder.
              </p>
              {error && <Alert tone="error">{error}</Alert>}
              <Button variant="outline" loading={busy} onClick={() => void sendLink()}>
                Send it again
              </Button>
            </>
          )}

          {step === 'confirm-email' && (
            <form onSubmit={confirmEmail} className="flex flex-col gap-4">
              <p>
                You opened the sign-in link on a different device or browser. Type the email address the invitation
                was sent to.
              </p>
              <Field label="Email" htmlFor="email">
                <TextInput
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={typedEmail}
                  onChange={(e) => setTypedEmail(e.target.value)}
                />
              </Field>
              {error && <Alert tone="error">{error}</Alert>}
              <Button type="submit" size="lg" loading={busy} className="w-full">
                Continue
              </Button>
            </form>
          )}

          {step === 'password' && details && (
            <form onSubmit={accept} className="flex flex-col gap-4">
              <p>
                You’re signed in as <strong className="break-all text-ink">{details.email}</strong>. Choose the
                password you’ll use to sign in to Checkover, including the iPhone app.
              </p>
              <Field
                label="Password"
                htmlFor="password"
                hint="At least 8 characters. Already use Checkover? Enter your current password to keep it."
              >
                <TextInput
                  id="password"
                  type="password"
                  required
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Field label="Confirm password" htmlFor="confirmPassword">
                <TextInput
                  id="confirmPassword"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Field>
              {error && <Alert tone="error">{error}</Alert>}
              {needsNewLink ? (
                <Button type="button" size="lg" loading={busy} onClick={() => void sendLink()} className="w-full">
                  Email me a new sign-in link
                </Button>
              ) : (
                <Button type="submit" size="lg" loading={busy} className="w-full">
                  {busy ? 'Joining…' : `Join ${companyLabel}`}
                </Button>
              )}
            </form>
          )}

          {step === 'done' && details && (
            <>
              <Alert tone="success">You’ve joined {companyLabel}.</Alert>
              <p>
                Sign in to the Checkover iPhone app with <strong className="break-all text-ink">{details.email}</strong>{' '}
                and the password you just chose.
              </p>
              <Button size="lg" loading={busy} onClick={() => void continueToApp()} className="w-full">
                Continue to Checkover
              </Button>
            </>
          )}
        </div>

        <p className="mt-5 text-center text-[13px] text-ink3">
          This invitation works once and expires after 7 days.
        </p>
      </div>
    </div>
  );
}
